import Vue from 'vue';
import * as types from '../mutation-types';
import api from '@/services';
import appConfig from '@/config';
import i18n from '@/localization';
import { Notify } from 'quasar';
import { Client } from '@stomp/stompjs';
import {
  GENERAL_EVENTS,
  INGAME_EVENTS,
  CONNECTIVITY_STATUSES,
} from '@/helpers';
import eventHub from '@/helpers/event-hub';

export const setStompConfig = ({ commit }, stompConfig) => {
  commit(types.APP_SET_STOMP_CONFIG, stompConfig);
};

export const toggleProcessing = ({ commit }, value) => {
  commit(types.APP_TOGGLE_PROCESSING, value);
};

export const setGameState = ({ commit }, gameState) => {
  commit(types.APP_SET_GAME_STATE, gameState);
};

export const getApiVersion = ({ commit }) => {
  // commit(types.APP_SET_API_VERSION, version);
};

export const closePredictions = ({ commit }, id) => {
  commit(types.APP_CLOSE_PREDICTIONS, id);
};

export const startPredictions = ({ commit }, competition) => {
  commit(types.APP_START_PREDICTIONS, competition);
};

export const postResults = ({ commit }, competition) => {
  commit(types.APP_POST_RESULTS, competition);
};

export const nextCompetition = ({ commit }, id) => {
  commit(types.APP_NEXT_COMPETITION, id);
};

export const cancelResults = ({ commit }, id) => {
  commit(types.APP_CANCEL_RESULTS, id);
};

export const endHalfInning = ({ commit }, game) => {
  commit(types.APP_END_HALF_INNING, game);
};

export const getPredictionsTypes = async ({ commit }) => {
  const response = await api.Duel.getPredictionsTypes();
  commit(types.APP_SET_PREDICTIONS_TYPES, response);
  return response;
};

export const getGames = async ({ commit }) => {
  const games = await api.Game.getGamesForToday();
  // const games = await api.Game.getGamesAll();
  commit(types.APP_SET_GAMES, games);
  return games;
};

export const initStomp = async ({ commit, getters, dispatch }, config) => {
  const {
    brokerUrl: brokerURL,
    brokerUsername: login,
    brokerPassword: passcode,
    updatesInboundTopic: topicIngame,
    personalUpdatesInboundTopic: topicPersonal,
    broadcastUpdatesInboundTopic: topicAll,
  } = config;

  const client = new Client({
    brokerURL,
    connectHeaders: {
      login,
      passcode,
    },
    debug(str) {
      if (appConfig.is_development) {
        console.log('debug', str);
      }
    },
  });

  const subscriptions = {
    all: null,
    ingame: null,
    personal: null,
  };

  const { activeDuelId, activeGameId, user } = getters;

  const connect = new Promise(resolve => {
    client.onConnect = () => {
      subscriptions.all = client.subscribe(
        `/topic/${topicAll}`,
        msg => {
          dispatch('processAllMessage', msg);
        },
        { priority: 9 }
      );

      if (activeDuelId && activeGameId) {
        subscriptions.ingame = client.subscribe(
          `/topic/${topicIngame}`,
          msg => {
            dispatch('processIngameMessage', msg);
          },
          {
            priority: 10,
            selector: `gameid = ${activeGameId}`,
          }
        );
        subscriptions.personal = client.subscribe(
          `/topic/${topicPersonal}`,
          msg => {
            dispatch('processPersonalMessage', msg);
          },
          {
            priority: 10,
            selector: `sid = '${user.sid}'`,
          }
        );
      }
      resolve();
    };
  });

  client.activate();

  commit(types.APP_SET_STOMP_STATE, {
    client,
    connect,
    subscriptions,
  });

  return connect;
};

export const unsubscribeActiveTopics = ({ commit, getters }) => {
  if (getters.stompSubscriptions.ingame) {
    getters.stompSubscriptions.ingame.unsubscribe();
  }
  if (getters.stompSubscriptions.personal) {
    getters.stompSubscriptions.personal.unsubscribe();
  }
  commit(types.APP_SET_STOMP_SUBSCRIPTION, {
    name: 'ingame',
    value: null,
  });
  commit(types.APP_SET_STOMP_SUBSCRIPTION, {
    name: 'personal',
    value: null,
  });
};

export const subscribeActiveTopics = (
  { dispatch, commit, getters },
  config
) => {
  const {
    updatesInboundTopic: topicIngame,
    personalUpdatesInboundTopic: topicPersonal,
  } = getters.stompConfig;
  getters.stompConnect.then(() => {
    dispatch('unsubscribeActiveTopics');
    const ingameSubscription = getters.stompClient.subscribe(
      `/topic/${topicIngame}`,
      msg => {
        dispatch('processIngameMessage', msg);
      },
      {
        priority: 10,
        selector: `gameid = ${getters.activeGameId}`,
      }
    );
    const personalSubscription = getters.stompClient.subscribe(
      `/topic/${topicPersonal}`,
      msg => {
        dispatch('processPersonalMessage', msg);
      },
      {
        priority: 10,
        selector: `sid = '${getters.user.sid}'`,
      }
    );
    commit(types.APP_SET_STOMP_SUBSCRIPTION, {
      name: 'ingame',
      value: ingameSubscription,
    });
    commit(types.APP_SET_STOMP_SUBSCRIPTION, {
      name: 'personal',
      value: personalSubscription,
    });
  });
};

export const processIngameMessage = async ({ dispatch }, msg) => {
  const { type, payload } = JSON.parse(msg.body);
  const { timestamp } = payload;

  switch (type) {
    case INGAME_EVENTS.STOP_PREDICTIONS:
      // competition already exists, so we change only it's STATUS field
      dispatch('closePredictions', payload.competition.id);
      Notify.create({
        color: 'primary',
        message: i18n.t('messages.predictions_stopped', [
          payload.competition.awayPlayer.name,
          payload.competition.homePlayer.name,
        ]),
        classes: 'text-center',
      });
      return;
    case INGAME_EVENTS.START_PREDICTIONS:
      // competition is created with STATUS = PENDING and START_PREDICTIONS action triggered after that, so at this moment we don't have this competition in vuex
      dispatch('startPredictions', payload.competition);
      Notify.create({
        color: 'primary',
        message: i18n.t('messages.predictions_started', [
          payload.competition.awayPlayer.name,
          payload.competition.homePlayer.name,
        ]),
        classes: 'text-center',
      });
      return;
    case INGAME_EVENTS.POST_RESULTS:
    case INGAME_EVENTS.VOID_RESULTS:
      dispatch('postResults', payload.competition);
      if (type === 'POST_RESULTS') {
        Notify.create({
          color: 'primary',
          message: i18n.t('messages.results_posted', [
            payload.competition.awayPlayer.name,
            payload.competition.homePlayer.name,
          ]),
          classes: 'text-center',
        });
      } else {
        Notify.create({
          color: 'warning',
          textColor: 'black',
          message: i18n.t('messages.results_voided', [
            payload.competition.awayPlayer.name,
            payload.competition.homePlayer.name,
          ]),
          classes: 'text-center',
        });
      }
      await dispatch('getGameState');
      return;
    case INGAME_EVENTS.NEXT_COMPETITION:
      dispatch('nextCompetition', payload.competition.id);
      return;
    case INGAME_EVENTS.CANCEL_RESULTS:
      dispatch('cancelResults', payload.competition.id);
      Notify.create({
        icon: 'cancel',
        color: 'warning',
        textColor: 'black',
        message: i18n.t('messages.cancel_results', [
          payload.competition.awayPlayer.name,
          payload.competition.homePlayer.name,
        ]),
      });
      await dispatch('getGameState');
      return;
    case INGAME_EVENTS.END_HALF_INNING:
      dispatch('endHalfInning', payload.game);
      return;
    default:
      console.log(`${type}, ${timestamp}`);
      console.log(payload);
      return;
  }
};

export const processPersonalMessage = async ({ dispatch }, msg) => {
  const { type, payload } = JSON.parse(msg.body);
  const { timestamp } = payload;

  switch (type) {
    case 'PREDICTION_ACCEPTED':
      Notify.create({
        color: 'positive',
        message: i18n.t('messages.prediction_accepted'),
        classes: 'text-center',
      });
      await dispatch('getGameState');
      break;
    case 'PREDICTION_DECLINED':
      let message;
      if (payload.errorMessage === 'COMPETITION_CLOSED') {
        message = i18n.t('messages.competition_closed');
      } else {
        message = i18n.t('messages.not_enough_points');
      }
      Notify.create({
        color: 'negative',
        message,
        classes: 'text-center',
      });
      await dispatch('getGameState');
      break;
  }
};

export const processAllMessage = async ({ dispatch, commit, getters }, msg) => {
  const { type, payload } = JSON.parse(msg.body);

  switch (type) {
    case GENERAL_EVENTS.GAME_UPDATE:
      if (+payload.game.id === getters.activeGameId) {
        await dispatch('getGameState');
      }
      commit(types.APP_UPDATE_GAME, payload.game);
      break;
    case GENERAL_EVENTS.DUELS_UPDATE:
      eventHub.$emit('duels-update', payload);
      break;
  }
};

export const loadDuelPageData = async (
  { state, commit, dispatch },
  payload
) => {
  const { id, duelId } = payload;
  const [duel, game] = await Promise.all([
    api.Duel.getDuel(duelId),
    api.Game.getGame(id),
  ]);
  commit(types.APP_SET_DUEL_PAGE_DATA, {
    duel,
    game,
  });
  return {
    duel,
    game,
  };
};

export const deactivateStomp = ({ commit }) => {
  commit(types.APP_STOMP_DEACTIVATE);
};

export const changeConnectivity = ({ commit }, status) => {
  switch (status) {
    case CONNECTIVITY_STATUSES.ONLINE:
      commit(types.APP_SET_CONNECTION_STATUS, CONNECTIVITY_STATUSES.ONLINE);
      break;
    case CONNECTIVITY_STATUSES.OFFLINE:
      commit(types.APP_SET_CONNECTION_STATUS, CONNECTIVITY_STATUSES.OFFLINE);
      break;
    case CONNECTIVITY_STATUSES.UNREACHABLE:
      commit(
        types.APP_SET_CONNECTION_STATUS,
        CONNECTIVITY_STATUSES.UNREACHABLE
      );
      break;
  }
};

export const setOfflineModalInstance = ({ commit }, payload) => {
  commit(types.APP_SET_OFFLINE_MODAL_INSTANCE, payload);
};
